import React, { useState, useEffect, useCallback } from 'react';
import ResponsiveSelect from '../../../../components/ResponsiveSelect';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  Divider
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { createBankFormField } from '../../../../modules/masterdata/actions';
import {
  selectCreateBankFormFieldLoading,
  selectCreateBankFormFieldSuccess
} from '../../../../modules/masterdata/selectors';

import { AddCircleOutline, Delete } from '@mui/icons-material';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

interface CreateBankFormModalProps {
  open: boolean;
  onClose: () => void;
}

const FIELD_TYPE_CHOICES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
];

const CreateBankFormModal: React.FC<CreateBankFormModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectCreateBankFormFieldLoading);
  const success = useSelector(selectCreateBankFormFieldSuccess);

  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [isRequired, setIsRequired] = useState(false);

  // For dropdown/radio options
  const [optionInput, setOptionInput] = useState('');
  const [fieldOptions, setFieldOptions] = useState<string[]>([]);

  const [errors, setErrors] = useState<{ fieldName?: string; fieldOptions?: string }>({});

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setFieldName('');
    setFieldType('text');
    setIsRequired(false);
    setOptionInput('');
    setFieldOptions([]);
    setErrors({});
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (success) {
      handleClose();
    }
  }, [success, handleClose]);

  const handleAddOption = () => {
    if (optionInput.trim()) {
      if (!fieldOptions.includes(optionInput.trim())) {
        setFieldOptions([...fieldOptions, optionInput.trim()]);
      }
      setOptionInput('');
    }
  };

  const handleDeleteClick = (option: string) => {
    setOptionToDelete(option);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (optionToDelete) {
      setFieldOptions(fieldOptions.filter(o => o !== optionToDelete));
    }
    setIsDeleteDialogOpen(false);
    setOptionToDelete(null);
  };

  const validate = () => {
    const newErrors: { fieldName?: string; fieldOptions?: string } = {};
    if (!fieldName.trim()) newErrors.fieldName = 'Field Name is required';

    if (['dropdown', 'radio', 'checkbox'].includes(fieldType) && fieldOptions.length === 0) {
      newErrors.fieldOptions = 'At least one option is required for this field type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      dispatch(createBankFormField({
        field_name: fieldName,
        field_type: fieldType,
        is_required: isRequired,
        field_options: ['dropdown', 'radio', 'checkbox'].includes(fieldType) ? fieldOptions : []
      }));
    }
  };

  const showOptionsInput = ['dropdown', 'radio', 'checkbox'].includes(fieldType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Bank Form Field</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Field Name"
            variant="outlined"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            error={!!errors.fieldName}
            helperText={errors.fieldName}
          />

          <ResponsiveSelect
            label="Field Type"
            value={fieldType}
            onChange={(val) => setFieldType(val)}
            options={FIELD_TYPE_CHOICES}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#5E8E7E',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#5E8E7E',
                  },
                }}
              />
            }
            label="Is Required"
          />

          {showOptionsInput && (
            <Box sx={{ border: '1px solid #eee', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Option value"
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddOption}
                  startIcon={<AddCircleOutline />}
                  sx={{
                    bgcolor: '#5E8E7E',
                    color: 'white',
                    '&:hover': { bgcolor: '#4A7A6A' },
                    textTransform: 'none',
                    px: 2
                  }}
                >
                  ADD
                </Button>
              </Box>

              {errors.fieldOptions && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mb: 1 }}>
                  {errors.fieldOptions}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {fieldOptions.map((option, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                      <Typography sx={{ color: '#333' }}>{option}</Typography>
                      <IconButton size="small" onClick={() => handleDeleteClick(option)} sx={{ color: '#E57373' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    {index < fieldOptions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: '#5E8E7E', '&:hover': { bgcolor: '#4A7A6A' }, px: 3 }}
        >
          {loading ? 'Adding...' : 'ADD FIELD'}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Option"
        message={`Are you sure you want to remove the option "${optionToDelete}"?`}
      />
    </Dialog>
  );
};

export default CreateBankFormModal;
